import { useEffect, useState } from "react";
import { HandCoins, Plus, Check } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { eurCents } from "@/components/FeesPanel";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

// Configuration des cotisations du groupe (Paramètres > Cotisations, admin)
export default function FeesConfig() {
  const { t } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [currentId, setCurrentId] = useState("");
  const [periods, setPeriods] = useState([]);
  const [form, setForm] = useState({ name: "", amount: "", due_date: "", description: "" });
  const [rib, setRib] = useState({ holder: "", iban: "", bic: "" });
  const [alertOn, setAlertOn] = useState(false);

  const load = async () => {
    const [cfg, p] = await Promise.all([api.get("/fees/config"), api.get("/fees/periods")]);
    setEnabled(cfg.data.enabled);
    setCurrentId(cfg.data.current_period?.id || "");
    setPeriods(p.data);
    setRib({ holder: cfg.data.rib_holder || "", iban: cfg.data.rib_iban || "", bic: cfg.data.rib_bic || "" });
    setAlertOn(!!cfg.data.alert_enabled);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const saveRib = async () => {
    try {
      await api.put("/fees/config", { rib_holder: rib.holder, rib_iban: rib.iban, rib_bic: rib.bic });
      toast.success(t("feescfg.config_saved"));
    } catch (e) { toast.error(e.friendly); }
  };

  const toggleAlert = async () => {
    try {
      const r = await api.put("/fees/config", { alert_enabled: !alertOn });
      setAlertOn(r.data.alert_enabled);
      toast.success(t("feescfg.config_saved"));
    } catch (e) { toast.error(e.friendly); }
  };

  const [editPeriod, setEditPeriod] = useState(null);
  const [editPForm, setEditPForm] = useState({ name: "", amount: "", due_date: "", description: "" });
  const [delPeriod, setDelPeriod] = useState(null);

  const submitEditPeriod = async () => {
    try {
      await api.put(`/fees/periods/${editPeriod.id}`, {
        name: editPForm.name,
        amount: editPForm.amount !== "" ? Number(editPForm.amount) : null,
        due_date: editPForm.due_date,
        description: editPForm.description,
      });
      toast.success(t("feescfg.period_updated"));
      setEditPeriod(null);
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  const submitDeletePeriod = async () => {
    try {
      await api.delete(`/fees/periods/${delPeriod.id}`);
      toast.success(t("feescfg.period_deleted"));
      setDelPeriod(null);
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  const toggle = async () => {
    try {
      const r = await api.put("/fees/config", { enabled: !enabled });
      setEnabled(r.data.enabled);
      toast.success(t("feescfg.config_saved"));
    } catch (e) { toast.error(e.friendly); }
  };

  const setCurrent = async (id) => {
    try {
      await api.put("/fees/config", { current_period_id: id });
      setCurrentId(id);
      toast.success(t("feescfg.config_saved"));
    } catch (e) { toast.error(e.friendly); }
  };

  const create = async () => {
    try {
      await api.post("/fees/periods", {
        name: form.name,
        amount: Number(form.amount),
        due_date: form.due_date || null,
        description: form.description || null,
      });
      toast.success(t("feescfg.created"));
      setForm({ name: "", amount: "", due_date: "", description: "" });
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="fees-config">
      <div className="app-card p-6 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="font-heading text-lg font-bold flex items-center gap-2"><HandCoins className="h-5 w-5 accent" /> {t("feescfg.enable")}</h3>
            <p className="text-xs t-secondary mt-1">{t("feescfg.enable_hint")}</p>
          </div>
          <button role="switch" aria-checked={enabled} onClick={toggle} data-testid="fees-enabled-switch"
            className={`w-12 h-7 rounded-full transition-colors relative shrink-0 ${enabled ? "brand-bg" : "bg-slate-600"}`}>
            <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${enabled ? "left-6" : "left-1"}`} />
          </button>
        </div>
      </div>

      <div className="app-card p-6 space-y-3" data-testid="fees-rib-card">
        <h3 className="font-heading text-lg font-bold">{t("feescfg.rib_title")}</h3>
        <div>
          <Label className="t-secondary text-xs uppercase tracking-wider">{t("feescfg.rib_holder")}</Label>
          <Input value={rib.holder} onChange={(e) => setRib({ ...rib, holder: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="fees-rib-holder-input" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="t-secondary text-xs uppercase tracking-wider">{t("feescfg.rib_iban")}</Label>
            <Input value={rib.iban} onChange={(e) => setRib({ ...rib, iban: e.target.value })} placeholder="FR76 …" className="mt-1 bg-transparent border-app font-mono" data-testid="fees-rib-iban-input" />
          </div>
          <div>
            <Label className="t-secondary text-xs uppercase tracking-wider">{t("feescfg.rib_bic")}</Label>
            <Input value={rib.bic} onChange={(e) => setRib({ ...rib, bic: e.target.value })} className="mt-1 bg-transparent border-app font-mono" data-testid="fees-rib-bic-input" />
          </div>
        </div>
        <Button className="brand-bg text-white" onClick={saveRib} data-testid="fees-rib-save">{t("feescfg.rib_save")}</Button>
      </div>

      <div className="app-card p-6 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="font-heading text-lg font-bold">{t("feescfg.alert")}</h3>
            <p className="text-xs t-secondary mt-1">{t("feescfg.alert_hint")}</p>
          </div>
          <button role="switch" aria-checked={alertOn} onClick={toggleAlert} data-testid="fees-alert-switch"
            className={`w-12 h-7 rounded-full transition-colors relative shrink-0 ${alertOn ? "brand-bg" : "bg-slate-600"}`}>
            <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${alertOn ? "left-6" : "left-1"}`} />
          </button>
        </div>
      </div>

      <div className="app-card p-6 space-y-3">
        <h3 className="font-heading text-lg font-bold">{t("feescfg.new_period")}</h3>
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("feescfg.name")} className="bg-transparent border-app" data-testid="fee-period-name-input" />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="t-secondary text-xs uppercase tracking-wider">{t("feescfg.amount")}</Label>
            <Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="fee-period-amount-input" />
          </div>
          <div>
            <Label className="t-secondary text-xs uppercase tracking-wider">{t("feescfg.due_date")}</Label>
            <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="fee-period-duedate-input" />
          </div>
        </div>
        <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={t("feescfg.description")} className="bg-transparent border-app" data-testid="fee-period-desc-input" />
        <Button className="brand-bg text-white" onClick={create} disabled={!form.name.trim() || form.amount === ""} data-testid="fee-period-create">
          <Plus className="h-4 w-4 mr-1" /> {t("feescfg.create")}
        </Button>
      </div>

      <div className="app-card p-6 space-y-2">
        <h3 className="font-heading text-lg font-bold">{t("feescfg.periods")}</h3>
        {periods.map((p) => (
          <div key={p.id} className="flex items-center justify-between gap-3 border-b border-app/50 py-2.5 last:border-0" data-testid={`fee-period-row-${p.id}`}>
            <div>
              <p className="text-sm font-medium">
                {p.name}
                {p.id === currentId && <span className="ml-2 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full brand-bg/15 brand border border-app">{t("feescfg.current")}</span>}
              </p>
              <p className="text-xs t-secondary">{eurCents(p.amount_cents)}{p.due_date ? ` — ${p.due_date}` : ""}</p>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {p.id !== currentId && (
                <Button size="sm" variant="outline" className="border-app text-xs" onClick={() => setCurrent(p.id)} data-testid={`fee-period-set-current-${p.id}`}>
                  <Check className="h-3.5 w-3.5 mr-1" /> {t("feescfg.set_current")}
                </Button>
              )}
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => { setEditPeriod(p); setEditPForm({ name: p.name, amount: String(p.amount_cents / 100), due_date: p.due_date || "", description: p.description || "" }); }} data-testid={`fee-period-edit-${p.id}`} title={t("feescfg.edit_period")} aria-label={t("feescfg.edit_period")}>
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-rose-400 hover:bg-rose-500/10" onClick={() => setDelPeriod(p)} data-testid={`fee-period-delete-${p.id}`} title={t("common.delete")} aria-label={t("common.delete")}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
        {!periods.length && <p className="text-xs t-secondary">{t("myfees.none")}</p>}
      </div>

      {editPeriod && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="fee-period-edit-dialog" onClick={() => setEditPeriod(null)}>
          <div className="app-card p-6 space-y-3 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-bold">{t("feescfg.edit_period")}</h3>
            <Input value={editPForm.name} onChange={(e) => setEditPForm({ ...editPForm, name: e.target.value })} placeholder={t("feescfg.name")} className="bg-transparent border-app" data-testid="fee-period-edit-name" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="t-secondary text-xs uppercase tracking-wider">{t("feescfg.amount")}</Label>
                <Input type="number" min="0" step="0.01" value={editPForm.amount} onChange={(e) => setEditPForm({ ...editPForm, amount: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="fee-period-edit-amount" />
              </div>
              <div>
                <Label className="t-secondary text-xs uppercase tracking-wider">{t("feescfg.due_date")}</Label>
                <Input type="date" value={editPForm.due_date} onChange={(e) => setEditPForm({ ...editPForm, due_date: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="fee-period-edit-duedate" />
              </div>
            </div>
            <Input value={editPForm.description} onChange={(e) => setEditPForm({ ...editPForm, description: e.target.value })} placeholder={t("feescfg.description")} className="bg-transparent border-app" data-testid="fee-period-edit-desc" />
            <p className="text-xs t-secondary">{t("feescfg.amount_note")}</p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" className="border-app" onClick={() => setEditPeriod(null)} data-testid="fee-period-edit-cancel">{t("common.cancel")}</Button>
              <Button className="brand-bg text-white" onClick={submitEditPeriod} disabled={!editPForm.name.trim() || editPForm.amount === ""} data-testid="fee-period-edit-confirm">{t("common.save")}</Button>
            </div>
          </div>
        </div>
      )}

      {delPeriod && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="fee-period-delete-dialog" onClick={() => setDelPeriod(null)}>
          <div className="app-card p-6 space-y-3 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-bold">{t("usr.delete_title_attr")}</h3>
            <p className="text-sm t-secondary">{delPeriod.name} — {eurCents(delPeriod.amount_cents)}. {t("feescfg.delete_period_q")}</p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" className="border-app" onClick={() => setDelPeriod(null)} data-testid="fee-period-delete-cancel">{t("common.cancel")}</Button>
              <Button className="bg-rose-500 text-white hover:bg-rose-600" onClick={submitDeletePeriod} data-testid="fee-period-delete-confirm">{t("common.delete")}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
